<template>
  <div class="dutch-auction">
    <div class="auction-info">
      <div class="first-block">
        <div class="line-info">
          <div class="info-title">
            Current Price
          </div>
          <div class="info current-price">
            {{ sellDetail.currentPrice | formatWeiAmount }}
            {{ sellDetail.currency | transformToCurrencyName }}
          </div>
        </div>
        <template v-if="isDutchAuctionMarket">
          <div class="line-info">
            <div class="info-title">
              Ending Price
            </div>
            <div class="info">
              {{ sellDetail.endingPrice | formatWeiAmount }}
              <template v-if="sellDetail.currency">
                {{ sellDetail.currency | transformToCurrencyName }}
              </template>
            </div>
          </div>
          <div>
            <div class="line-info">
              <div class="info-title">
                Expiration Date
              </div>
              <div class="info">
                {{ sellDetail.expirationTimeData }}
              </div>
            </div>
          </div>
          <div class="line-info">
            <div class="info-title">
              Decline Interval
            </div>
            <div class="info">
              Every {{ sellDetail.decliningInterval / 3600 }} hours
            </div>
          </div>
        </template>
      </div>
    </div>
    <div v-if="myAddress" class="button-wrapper">
      <PrimaryButton
        class="custom-button"
        :is-disable="!isBuyable"
        @onClick="onOpenBuyNFT"
      >
        <img class="button-icon" src="@/assets/icon/cart-icon.svg" />
        Buy
      </PrimaryButton>
      <SecondaryButton
        v-if="nft.isEnableOffering"
        class="custom-button offer-button"
        @onClick="onOpenMakeAnOffer"
      >
        <img class="button-icon" src="@/assets/icon/dollar-rounded-icon.svg" />
        Make an Offer
      </SecondaryButton>
    </div>
    <div v-else class="button-wrapper">
      <PrimaryButton class="custom-button" @onClick="toggleChooseWallet(true)">
        Connect Wallet
      </PrimaryButton>
    </div>
    <BuyNFTModal
      ref="buyNFT"
      :market-contract="marketContract"
      :nft="nft"
      :seller-address="sellerAddress"
    />
    <MakeAnOfferModal
      ref="makeAnOffer"
      :market-contract="marketContract"
      :nft="nft"
      :seller-address="sellerAddress"
      @handleRefreshData="handleRefreshData"
    />
    <BaseCustomModal
      v-if="isShowChooseWallet"
      :is-corner="true"
      @closeModal="toggleChooseWallet(false)"
    >
      <choose-wallet @close-choose-wallet-modal="toggleChooseWallet(false)" />
    </BaseCustomModal>
  </div>
</template>

<script>
import PrimaryButton from '@/components/BaseComponents/PrimaryButton.vue';
import SecondaryButton from '@/components/BaseComponents/SecondaryButton.vue';
import MakeAnOfferModal from '@/modal/MakeAnOfferModal';
import BuyNFTModal from '@/modal/BuyNFTModal';
import {
  getMarketPriceOfNft,
  getDutchAuctionListing,
  getDutchAuctionFinalPrice
} from '@/helpers/utils';
import BigNumber from 'bignumber.js';
import config from '@/config';
import moment from 'moment';
import { MARKET_ADDRESSES } from '@/constans';
import { ChooseWallet } from '@/modal';
import BaseCustomModal from '@/components/BaseCustomModal.vue';

export default {
  components: {
    PrimaryButton,
    SecondaryButton,
    MakeAnOfferModal,
    BuyNFTModal,
    BaseCustomModal,
    ChooseWallet
  },
  props: {
    isBuyable: {
      type: Boolean,
      default: false
    },
    marketContract: {
      type: String,
      default: ''
    },
    nft: {
      type: Object,
      default: () => ({})
    },
    sellerAddress: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isShowChooseWallet: false,
      sellDetail: {
        currency: '',
        currentPrice: 0,
        endingPrice: 0,
        expirationTimeData: null,
        decliningInterval: 0
      }
    };
  },
  computed: {
    myAddress() {
      return this.$store.state.auth.address;
    },
    nftContract() {
      return this.nft.contract;
    },
    nftId() {
      return this.nft.id;
    },
    isEnableOffering() {
      return this.nft.isEnableOffering;
    },
    isArtWork() {
      return this.nftContract === config.addresses.artworkNftAddress;
    },
    isDutchAuctionMarket() {
      return this.marketContract === MARKET_ADDRESSES.dutchAuctionMarketplace;
    }
  },
  watch: {
    nftContract() {
      if (this.isDutchAuctionMarket) {
        this.getDutchAuctionListing();
        this.getDutchAuctionFinalPrice();
      } else {
        this.fetchOnChainCurrentPrice();
      }
    }
  },
  mounted() {
    if (this.isDutchAuctionMarket) {
      this.getDutchAuctionListing();
      this.getDutchAuctionFinalPrice();
    } else {
      this.fetchOnChainCurrentPrice();
    }
  },
  methods: {
    toggleChooseWallet(isShow) {
      this.isShowChooseWallet = isShow;
    },
    toast(message) {
      this.$toasted
        .show(message, {
          className: 'custom-toast',
          action: {
            text: 'CANCEL',
            onClick: (e, toastObject) => {
              toastObject.goAway(0);
            }
          }
        })
        .goAway(3000);
    },
    async fetchOnChainCurrentPrice() {
      if (!this.nftContract || !this.nftId) return;
      const [price, currency] = await getMarketPriceOfNft(
        this.nftContract,
        this.nftId
      );
      this.$set(this.sellDetail, 'currentPrice', price);
      this.$set(this.sellDetail, 'currency', currency);
    },
    async getDutchAuctionListing() {
      if (!this.nftContract || !this.nftId) return;
      const {
        endingPrice,
        expirationTime,
        decliningInterval
      } = await getDutchAuctionListing(this.nftContract, this.nftId);
      const expirationTimeAsString = moment(expirationTime * 1000)
        .utc()
        .format('DD-MM-YYYY HH:mm:ss');
      this.$set(this.sellDetail, 'decliningInterval', decliningInterval);
      this.$set(
        this.sellDetail,
        'endingPrice',
        new BigNumber(endingPrice._hex)
      );
      this.$set(
        this.sellDetail,
        'expirationTimeData',
        `${expirationTimeAsString} (UTC)`
      );
    },
    async getDutchAuctionFinalPrice() {
      if (!this.nftContract || !this.nftId || !this.isDutchAuctionMarket)
        return;
      const [price, currency] = await getDutchAuctionFinalPrice(
        this.nftContract,
        this.nftId
      );

      this.$set(this.sellDetail, 'currentPrice', price.toString());
      this.$set(this.sellDetail, 'currency', currency);
    },
    handleRefreshData() {
      this.$emit('handleRefreshData');
    },
    onOpenMakeAnOffer() {
      this.$bvModal.show('make-offer-modal');
      this.$refs.makeAnOffer.init();
    },
    onOpenBuyNFT() {
      this.$bvModal.show('purchase-modal');
      this.$refs.buyNFT.init();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/config/_variables';
.test {
  background-color: red;
}
.dutch-auction {
  font-family: Inter;
  font-size: 16px;
  .auction-info {
    display: flex;
    justify-content: flex-start;
    .first-block,
    .second-block {
      .line-info {
        display: flex;
        .info-title {
          min-width: 140px;
          color: #828c9f;
        }
        .info {
          font-family: Inter-Medium;
          color: #203a54;
          &.current-price {
            color: #5b6ef5;
          }
        }
      }
    }
    .second-block {
      width: 50%;
    }
  }
  .button-wrapper {
    margin-top: 24px;
    display: flex;

    .custom-button {
      width: 180px;
      margin-right: 16px;
      display: flex;
      align-items: center;
      &.offer-button {
        &:hover {
          .button-icon {
            filter: brightness(0) invert(1);
          }
        }
      }
      .button-icon {
        margin-right: 8px;
      }
    }
  }
}
@media screen and (max-width: $breakpoint-mobile) {
  .dutch-auction {
    .auction-info {
      flex-wrap: wrap;
      justify-content: center;
    }
    .button-wrapper {
      flex-wrap: wrap;
      justify-content: center;
      .custom-button {
        width: 140px;
        font-size: 15px;
        white-space: nowrap;
        max-height: 48px;
        margin: 8px 4px;
      }
    }
  }
}
</style>
